import React, { useState, useEffect } from 'react';
import BackButton from '../../components/common/UI/BackButton';
import SuccessModal from '../../components/common/UI/SuccessModal';
import { useDeposit } from '../../hooks/useDeposit';
import './BankDeposit.css';

const BankTransactionForm = ({ bank, onSubmit, onBack }) => {
  const { createBankDeposit, getDepositDescription, isSuccess, successDetails, showSuccess, hideSuccess } = useDeposit();
  const [formData, setFormData] = useState({
    amount: '',
  });
  const [depositDescription, setDepositDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [loadingDescription, setLoadingDescription] = useState(true);

  // ✅ Fetch current deposit description on mount
  useEffect(() => {
    const fetchDescription = async () => {
      try {
        console.log('🔄 Fetching deposit description for form...');
        const result = await getDepositDescription();
        setDepositDescription(result.deposit_description);
        console.log('✅ Description loaded:', result.deposit_description);
      } catch (error) {
        console.error('❌ Error loading description:', error);
        setDepositDescription('Error loading description');
      } finally {
        setLoadingDescription(false);
      }
    };

    fetchDescription();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Validate amount
    if (!formData.amount) {
      errors.amount = 'Amount is required';
    } else {
      const numAmount = parseFloat(formData.amount);
      if (isNaN(numAmount)) {
        errors.amount = 'Invalid amount format';
      } else if (numAmount < 2000) {
        errors.amount = 'Minimum amount is ₦2,000';
      } else if (numAmount > 100000000) {
        errors.amount = 'Amount exceeds maximum limit';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 Bank form submitted with data:', formData);

    // Validate form before submission
    if (!validateForm()) {
      console.log('❌ Form validation failed:', validationErrors);
      return;
    }

    setSubmitting(true);

    try {
      console.log('📤 Creating bank deposit...');
      const response = await createBankDeposit({
        amount: parseFloat(formData.amount),
        bank_name: bank.name,
        account_name: bank.accountName,
        account_number: bank.accountNumber,
        // ✅ No deposit_reference sent - auto-generated on backend
      });

      console.log('✅ Bank deposit API response:', response);

      // ✅ Update deposit description if backend returned a new one
      if (response.new_deposit_description) {
        setDepositDescription(response.new_deposit_description);
        console.log('🔄 Updated to new deposit description:', response.new_deposit_description);
      }

      // Show success modal
      showSuccess({
        'Amount': `₦${parseFloat(formData.amount).toLocaleString()}`,
        'Bank': bank.name,
        'Account Name': bank.accountName,
        'Account Number': bank.accountNumber,
        'Description Used': response.deposit?.deposit_reference || depositDescription,
        'Reference ID': response.deposit?.id || 'N/A',
        'Status': 'Pending Review'
      });

      // Clear form
      setFormData({
        amount: '',
      });
      setValidationErrors({});

    } catch (error) {
      console.error('❌ Deposit failed:', error);
      alert(`failed: ${error.message || 'Please try again.'}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bank-transaction-form">
      <div className="section-header">
        <BackButton onClick={onBack} />
        <h2>Bank Transfer Details</h2>
      </div>

      <form onSubmit={handleSubmit} className="deposit-form">
        <div className="bank-details-section">
          <h3 className="section-title">Transfer to:</h3>
          <div className="bank-details-card">
            <div className="bank-name-large">{bank.name}</div>
            <div className="account-details">
              <div className="account-name">{bank.accountName}</div>
              <div className="account-number">{bank.accountNumber}</div>
            </div>
            <p className="instruction">
              Make sure to use your unique description when transferring.
            </p>
          </div>
        </div>

        {/* ✅ NEW: Display auto-generated description (read-only) */}
        <div className="form-group">
          <label htmlFor="description">Your Transfer Description <span className="required">*</span></label>
          {loadingDescription ? (
            <div className="description-loading-inline">
              <span className="spinner-small"></span> Loading...
            </div>
          ) : (
            <div className="description-display">
              <code className="description-value">{depositDescription}</code>
            </div>
          )}
          <p className="input-help">
            Use this description in your bank transfer narration/description field.
          </p>
        </div>

        <div className="form-group">
          <label htmlFor="amount">Amount (₦) <span className="required">*</span></label>
          <input
            type="number"
            id="amount"
            value={formData.amount}
            onChange={(e) => handleInputChange('amount', e.target.value)}
            placeholder="0.00"
            step="0.01"
            min="2000"
            required
          />
          {validationErrors.amount && (
            <p className="validation-error">⚠️ {validationErrors.amount}</p>
          )}
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="submit-deposit-btn"
            disabled={submitting || loadingDescription}
          >
            {submitting ? 'Submitting...' : 'Fund account'}
          </button>
        </div>
      </form>

      {/* Success Modal */}
      <SuccessModal
        isOpen={isSuccess}
        onClose={hideSuccess}
        title="Bank Transfer Received!"
        message="Your request has been received and is being processed."
        details={successDetails}
        onContinue={() => {
          hideSuccess();
          window.location.href = '/transaction-history';
        }}
      />
    </div>
  );
};

export default BankTransactionForm;